import * as React from "react"
import { type EntityTransaction, type Transaction } from "@digits-graphql/frontend/graphql-bearer"
import { LoadingBlock } from "@digits-shared/components/Loaders"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgSwitchHorizontal01 } from "@digits-shared/components/SVGIcons/line/SwitchHorizontal01.svg"
import { ColorIconStyled, getIconColor } from "@digits-shared/components/UI/Elements/ColorIcon"
import { IconContainer, IconSize, IconSVGContainer } from "@digits-shared/components/UI/Icons/Icon"
import { Amount } from "@digits-shared/components/UI/Table/Content"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import moneyFlowHelper from "@digits-shared/helpers/moneyFlowHelper"
import { CurrencyStyle } from "@digits-shared/helpers/numberHelper"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import {
  ListItem,
  type SummaryListData,
  TopList,
} from "src/frontend/components/Shared/Layout/Components/Lists/WidgetSummaryList"
import { CategoryIconContainer } from "src/frontend/components/Shared/Transactions/Icons/CategoryIcon"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import { useTextIsOverflowing } from "src/frontend/hooks/useTextIsOverflowing"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import { PartyIcon } from "src/shared/components/PartyHover/PartyIcon"
import transactionHelper from "src/shared/helpers/transactionHelper"

/*
  STYLES
*/

const IconStyled = styled.div`
  display: flex;
  align-items: center;

  ${IconContainer} {
    margin: 0;
  }

  ${CategoryIconContainer} {
    height: 24px;
    width: 24px;
  }

  ${ColorIconStyled} {
    font-size: 8px;
  }

  ${IconSVGContainer} {
    padding: 2px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`

const StyledAmount = styled(Amount)`
  text-align: right;
  padding: 8px 0 7px;

  font-size: 11px;
  font-weight: ${fonts.weight.medium};
`

export const Date = styled.div`
  color: ${colors.secondary50};
  font-weight: ${fonts.weight.heavy};
  text-transform: uppercase;
  font-size: 10px;
  margin-right: 10px;
  white-space: nowrap;
`

const List = styled(TopList)`
  ${ListItem} {
    @container component-container (min-width: 350px) {
      grid-template-columns: 0 75px 1fr auto 0;
    }

    @container component-container (max-width: 350px) {
      ${Date} {
        display: none;
      }
    }
  }
`

export const RowContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export const Title = styled.div`
  color: ${colors.secondary};
  font-size: 12px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-weight: ${fonts.weight.normal};
`

export const Description = styled.div`
  color: ${colors.secondary50};
  font-size: 11px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-weight: ${fonts.weight.book};
`

const Month = styled.div`
  margin-top: 1px;
`

const Year = styled.div`
  font-weight: ${fonts.weight.book};
`

const TransferIconContainer = styled(IconContainer)`
  background-color: ${getIconColor().background};
  width: 20px;
  height: 20px;
  margin-right: 10px;
`

const TransferIcon = styled(SvgSwitchHorizontal01)`
  width: 14px;
  height: 14px;
  ${svgPathStyles(colors.white, 1.5)};
`

const TransferRow = styled.div`
  font-size: 11px;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`

const TransferText = styled.span`
  margin: 0 5px;
  opacity: 0.5;
  flex-shrink: 0;
  font-size: 11px;
`

/*
  INTERFACES
*/

// TODO: Unify on EntityTransaction if we get a dedicated go-services endpoint
//       for the live data fetch
type DisplayableTransaction = Transaction | EntityTransaction

interface TopTransactionsListProps {
  transactions: DisplayableTransaction[] | undefined
  loading: boolean
  className?: string
}

/*
  COMPONENT
*/

export const TopTransactionsList = React.memo<TopTransactionsListProps>(
  ({ transactions, loading, className }) => {
    const generatePath = useFrontendPathGenerator()
    const { isSharingContextActive } = useSession<FrontendSession>()

    const linkForItem = React.useCallback(
      (data: Transaction) =>
        generatePath(routes.accountingDetailEdit, {
          transactionId: data.factId,
        }),
      [generatePath]
    )

    return (
      <List
        className={className}
        data={transactions}
        loading={loading}
        activeIndex={undefined}
        IconComponent={Icon}
        LabelComponent={TransactionLabel}
        SummaryComponent={TransactionAmount}
        linkForItem={isSharingContextActive ? undefined : linkForItem}
      />
    )
  }
)

const Icon: React.FC<SummaryListData<DisplayableTransaction>> = ({ data }) => {
  if (!data) return null

  const { party, timestamp } = data
  const date = dayjs.unix(timestamp).utc()

  const icon = transactionHelper.isTransfer(data) ? (
    <TransferIconContainer>
      <TransferIcon />
    </TransferIconContainer>
  ) : (
    <PartyIcon size={IconSize.Small} party={party} css="margin-right: 10px;" disableHoverBorder />
  )

  return (
    <IconStyled>
      <Date>
        <Month>{date.format("MMM DD")}</Month>
        <Year>{date.format("YYYY")}</Year>
      </Date>
      {icon}
    </IconStyled>
  )
}

const TransactionLabel: React.FC<SummaryListData<DisplayableTransaction>> = ({ data }) => {
  const { textElementRef, isTextOverflowing } = useTextIsOverflowing<HTMLDivElement>()
  if (!data) return <LoadingBlock width="100px" height="30px" />

  if (transactionHelper.isTransfer(data)) {
    return (
      <TransferRow>
        <TransferText css="margin-left:0;">Transfer from</TransferText>
        {data.displayCategory?.name ?? "Uncategorized"}
        <TransferText css="margin-right: 0;">to</TransferText>{" "}
        {data.splitCategory?.name ?? "Uncategorized"}
      </TransferRow>
    )
  }

  const { party } = data
  const title = party?.name || data.description || data.name || ""

  return (
    <RowContent>
      <DigitsTooltip
        css=" overflow: hidden"
        content={<div>{title}</div>}
        disabled={!isTextOverflowing}
      >
        <Title ref={textElementRef}>{title}</Title>
      </DigitsTooltip>
      <Description>{data.displayCategory.name || "Uncategorized"}</Description>
    </RowContent>
  )
}

const TransactionAmount: React.FC<SummaryListData<DisplayableTransaction>> = ({ data }) => {
  if (!data) return null

  const { moneyFlow } = data

  const currency = moneyFlowHelper.currency(moneyFlow, {
    style: CurrencyStyle.Detail,
  })

  return (
    <StyledAmount flow={moneyFlow.businessFlow} normal={moneyFlow.isNormal}>
      {currency}
    </StyledAmount>
  )
}
